import React, { useEffect, useState, useRef } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { X, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxjss3TgDQN6ihmsA6XV324S4E1Qh-L3Gn0sa5wCh8JWmOo15dZjogIMINaX5rVwLsN/exec';
export function FinalCTA() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [formErrors, setFormErrors] = useState({
    phone: '',
    email: ''
  });
  const scrollPositionRef = useRef(0);
  const openModal = () => {
    scrollPositionRef.current = window.scrollY;
    setIsModalOpen(true);
    setSubmitStatus('idle');
    setFormErrors({
      phone: '',
      email: ''
    });
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollPositionRef.current}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isModalOpen]);
  const validateForm = (): boolean => {
    const errors = {
      phone: '',
      email: ''
    };
    let isValid = true;
    // Phone validation: must be 10, 12, or 13 characters (digits only)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10 && phoneDigits.length !== 12 && phoneDigits.length !== 13) {
      errors.phone = 'Номер телефону має містити 10, 12 або 13 цифр';
      isValid = false;
    }
    // Email validation: must contain @ if provided
    if (formData.email && !formData.email.includes('@')) {
      errors.email = 'Email має містити символ @';
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      // Send data to Google Sheets via Apps Script
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          source: 'FinalCTA - Отримати консультацію',
          timestamp: new Date().toISOString()
        })
      });
      // Since mode is 'no-cors', we can't read the response
      // We assume success if no error was thrown
      setSubmitStatus('success');
      // Reset form after short delay to show success message
      setTimeout(() => {
        closeModal();
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: ''
        });
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <>
      <Section className="bg-transparent py-16 md:py-24" data-id="element-137">
        <div className="max-w-4xl mx-auto text-center px-4" data-id="element-138">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-139">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 md:mb-6" data-id="element-140">
              Почніть керувати зростанням бізнесу{' '}
              <span className="text-blue-400" data-id="element-141">усвідомлено</span>
            </h2>

            <p className="text-lg md:text-xl text-gray-400 mb-3 md:mb-4" data-id="element-142">
              Перший модуль напряму «Шлях лідера»
            </p>

            <p className="text-xl md:text-2xl font-semibold text-blue-400 mb-8 md:mb-10" data-id="element-143">
              Marketing · Growth Acquisition
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8 md:mb-10" data-id="element-144">
              <Button href="#tariffs" variant="primary" size="lg" withArrow data-id="element-145">
                Забронювати місце
              </Button>
              <Button variant="outline" size="lg" onClick={openModal} data-id="element-146">
                Отримати консультацію
              </Button>
            </div>

            <div className="text-gray-400" data-id="element-147">
              <p className="mb-2 text-sm md:text-base" data-id="element-148">Залишилися питання?</p>
              <a href="https://t.me/armarsolutions" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm md:text-base" data-id="element-149">
                <Send className="w-4 h-4" data-id="element-150" />
                Пишіть нам у Telegram
              </a>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Consultation Modal - with scroll lock */}
      <AnimatePresence data-id="element-151">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={closeModal} style={{
        touchAction: 'none'
      }} data-id="element-152">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0A0F2A] to-[#1E2A5E] rounded-2xl border border-white/10 shadow-2xl p-6 md:p-8" onClick={e => e.stopPropagation()} data-id="element-153">
              {/* Close Button */}
              <button onClick={closeModal} className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white" data-id="element-154">
                <X className="w-5 h-5" data-id="element-155" />
              </button>

              <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-156">
                Отримати консультацію
              </h3>
              <p className="text-gray-400 mb-6 text-sm md:text-base" data-id="element-157">
                Залиште свої дані і ми зв'яжемося з вами
              </p>

              {submitStatus === 'success' ? <div className="text-center py-8" data-id="element-158">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-159">
                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-160">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-161" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2" data-id="element-162">
                    Дякуємо!
                  </h4>
                  <p className="text-gray-400" data-id="element-163">
                    Ми зв'яжемося з вами найближчим часом
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-164">
                  <div data-id="element-165">
                    <label className="block text-sm text-gray-400 mb-2" data-id="element-166">
                      Ім'я та прізвище *
                    </label>
                    <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-base" placeholder="Введіть ваше ім'я" required disabled={isSubmitting} data-id="element-167" />
                  </div>

                  <div data-id="element-168">
                    <label className="block text-sm text-gray-400 mb-2" data-id="element-169">
                      Номер телефону *
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => {
                setFormData({
                  ...formData,
                  phone: e.target.value
                });
                if (formErrors.phone) {
                  setFormErrors({
                    ...formErrors,
                    phone: ''
                  });
                }
              }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.phone ? 'border-red-500' : 'border-white/20 focus:border-blue-500'}`} placeholder="+380" required disabled={isSubmitting} data-id="element-170" />
                    {formErrors.phone && <p className="text-red-400 text-sm mt-1" data-id="element-171">
                        {formErrors.phone}
                      </p>}
                  </div>

                  <div data-id="element-172">
                    <label className="block text-sm text-gray-400 mb-2" data-id="element-173">
                      Email
                    </label>
                    <input type="text" value={formData.email} onChange={e => {
                setFormData({
                  ...formData,
                  email: e.target.value
                });
                if (formErrors.email) {
                  setFormErrors({
                    ...formErrors,
                    email: ''
                  });
                }
              }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.email ? 'border-red-500' : 'border-white/20 focus:border-blue-500'}`} placeholder="email@example.com" disabled={isSubmitting} data-id="element-174" />
                    {formErrors.email && <p className="text-red-400 text-sm mt-1" data-id="element-175">
                        {formErrors.email}
                      </p>}
                  </div>

                  <div data-id="element-176">
                    <label className="block text-sm text-gray-400 mb-2" data-id="element-177">
                      Назва компанії
                    </label>
                    <input type="text" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-base" placeholder="Назва вашої компанії" disabled={isSubmitting} data-id="element-178" />
                  </div>

                  {submitStatus === 'error' && <p className="text-red-400 text-sm text-center" data-id="element-179">
                      Виникла помилка. Спробуйте ще раз.
                    </p>}

                  <Button type="submit" variant="primary" fullWidth className="mt-6" disabled={isSubmitting} data-id="element-180">
                    {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-181">
                        <Loader2 className="w-5 h-5 animate-spin" data-id="element-182" />
                        Надсилання...
                      </span> : 'Надіслати заявку'}
                  </Button>
                </form>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}